// Der gemeinsame Zustandsblock fuer "leer", "Fehler" und "nicht da"
// (ST-UX-001, Slice 9; NFR-UX-004/005, CM-UX-010, NFR-ACC-001).
//
// Ein Muster statt je Bildschirm eines: Titel, was los ist und was erhalten
// bleibt, und GENAU EINE Hauptaktion. Die Texte kommen von den Bildschirmen
// (PlainMessage, access.ts); dieser Baustein gibt ihnen nur Gestalt.

import { useEffect, useRef, useState, type MouseEvent, type ReactElement, type RefObject } from 'react';
import { Body1, Body1Strong, Button, Caption1, makeStyles, mergeClasses, tokens, typographyStyles } from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { useSeitenueberschrift, useSeitenueberschriftVorrang } from './layout.tsx';
import { navigate } from './router.tsx';

export type ZustandsArt = 'leer' | 'fehler' | 'nicht-da';

export type ZustandsAktion =
  | { readonly label: string; readonly onClick: () => void; readonly icon?: ReactElement | undefined }
  | { readonly label: string; readonly href: string; readonly icon?: ReactElement | undefined };

export interface ZustandProps {
  readonly art: ZustandsArt;
  readonly titel: string;
  readonly saetze: readonly string[];
  /** Fehlt nur, wo die Hauptaktion unmittelbar darueber steht (Verlauf ohne Notiz). */
  readonly haupt?: ZustandsAktion;
  readonly neben?: ZustandsAktion;
  /**
   * Ebene der Titelueberschrift; die Vorgabe ist die naechste unter der
   * Seitenueberschrift. Eingebettet ist der Titel Text, ausser eine Ebene ist
   * genannt: dann eine Ueberschrift in derselben Optik, damit ein
   * Screenreader den Zustand anspringt (Kontaktliste ohne Treffer).
   */
  readonly ebene?: 2 | 3 | 4;
  /** Block ist eine Statusregion -- nur, wo heute schon eine Region den Satz traegt. */
  readonly status?: boolean;
  /**
   * Abschnitt einer Seite mit eigener Hauptaktion (Kontaktseite): Aktion
   * secondary, Titel als Text statt Ueberschrift, kein Zustandssymbol (die Aktion darf eines tragen), linksbuendig.
   * Auf der Seite bleibt so genau EIN primary (Michael, 06.10.2026).
   */
  readonly eingebettet?: boolean;
  /** Fokus auf den Titel, wenn der Zustand NACH einer Handlung erscheint (WCAG 2.4.3). */
  readonly fokus?: boolean;
  /**
   * Die Vorgangsnummer, auf die ein Satz verweist (PlainMessage.correlationId).
   * Sie steht unter den Aktionen (v4), damit die Nutzerin sie der Administration
   * nennen kann (NFR-UX-004), ohne die technische Sicht zu oeffnen.
   */
  readonly vorgangsnummer?: string | undefined;
  /**
   * Der Zustand ist die ganze Seite (h1 plus Karte), wie "nicht da" stets: etwa
   * die gestoerte Kontaktseite, an der sonst nichts steht (DESIGN-P3 #540).
   */
  readonly seite?: boolean;
}

const useStyles = makeStyles({
  // v4 .state: mittig in der Karte, grosszuegiger Abstand, Saetze gedaempft und hoechstens 46 Zeichen breit.
  block: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.625rem',
    textAlign: 'center',
    maxWidth: '36rem',
    marginInline: 'auto',
    paddingBlock: '2.5rem',
    paddingInline: '1rem',
    boxSizing: 'border-box',
    overflowWrap: 'anywhere',
  },
  eingebettet: { alignItems: 'flex-start', textAlign: 'start', maxWidth: 'none', marginInline: 0, paddingBlock: 0, paddingInline: 0 },
  eingebetteteSaetze: { alignItems: 'flex-start', maxWidth: 'none', '& > *': { textAlign: 'start' } },
  eingebettetAktionen: { justifyContent: 'flex-start', marginTop: '0.25rem' },
  eingebetteterTitel: { ...typographyStyles.body1Strong, margin: 0, ':focus': { outline: 'none' }, ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' } },
  // Die Seite traegt die h1; der Zustand ist eine Abschnittsueberschrift (v4 .state h2), nicht eine zweite Seitenueberschrift.
  titel: { ...typographyStyles.subtitle1, margin: 0, ':focus': { outline: 'none' }, ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' } },
  // Fluents Text setzt die Ausrichtung selbst; mehrzeilige Saetze stehen sonst linksbuendig im mittigen Block.
  saetze: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', maxWidth: '46ch', color: tokens.colorNeutralForeground2, '& > *': { textAlign: 'center', color: tokens.colorNeutralForeground2 } },
  // Ein Fehler sagt es im Titel zusaetzlich in Farbe (v4 .state.err h2); das Wort traegt die Aussage.
  titelFehler: { color: tokens.colorStatusDangerForeground1 },
  aktionen: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '0.75rem',
    marginTop: '0.75rem',
  },
  // v4 .state: kleine gedaempfte Zeile unter den Aktionen, die Nummer in Festbreite.
  vorgang: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', columnGap: '0.5rem', color: tokens.colorNeutralForeground2 },
  eingebettetVorgang: { justifyContent: 'flex-start' },
  // Die Nummer rueckt als Ganzes in die naechste Zeile; brechen darf sie nur, wenn sie laenger als die Zeile ist (64 Zeichen bei 320 px).
  nummer: { display: 'inline-block', maxWidth: '100%', overflowWrap: 'anywhere', fontFamily: tokens.fontFamilyMonospace, userSelect: 'all' },
  verborgen: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  // "Nicht da" als Seite (v4 v13): der Titel ist die h1 im Seitenkopf, der
  // Zustand steht darunter in einer Karte -- wie die Seite, die es nicht gibt.
  seitentitel: {
    margin: '0.25rem 0 0',
    fontSize: tokens.fontSizeBase600,
    lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightSemibold,
    overflowWrap: 'anywhere',
    ':focus': { outline: 'none' },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  karte: {
    marginTop: '1rem',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
  },
  symbolFehler: { color: tokens.colorStatusWarningForeground1 },
  symbolRuhig: { color: tokens.colorNeutralForeground2 },
});

/** 32-px-Symbol, nur Zierde: der Titel traegt die Aussage. */
function Symbol({ art }: { readonly art: ZustandsArt }): ReactElement {
  const own = useStyles();
  const gemeinsam = { viewBox: '0 0 32 32', width: 32, height: 32, 'aria-hidden': true, focusable: false, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
  if (art === 'leer') {
    return (
      <svg {...gemeinsam} className={own.symbolRuhig}>
        <circle cx="16" cy="16" r="12" />
        <path d="m10.5 16.5 4 4 7-8" />
      </svg>
    );
  }
  if (art === 'fehler') {
    return (
      <svg {...gemeinsam} className={own.symbolFehler}>
        <path d="M16 4 29 27H3z" />
        <path d="M16 13v7" />
        <path d="M16 23.5v.01" />
      </svg>
    );
  }
  return (
    <svg {...gemeinsam} className={own.symbolRuhig}>
      <circle cx="14" cy="14" r="9" />
      <path d="m21 21 7 7" />
      <path d="M11.5 11.5a2.7 2.7 0 1 1 3.6 2.5c-.7.3-1.1.9-1.1 1.6" />
      <path d="M14 18.5v.01" />
    </svg>
  );
}

function Aktion({ aktion, haupt }: { readonly aktion: ZustandsAktion; readonly haupt: boolean }): ReactElement {
  const appearance = haupt ? 'primary' : 'secondary';
  if ('href' in aktion) {
    const href = aktion.href;
    // Navigation bleibt ein Link (Rolle "link"), hat aber Knopfgestalt.
    const beiKlick = (ereignis: MouseEvent<HTMLElement>): void => {
      if (ereignis.defaultPrevented || ereignis.button !== 0 || ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey || ereignis.altKey) {
        return;
      }
      ereignis.preventDefault();
      navigate(href);
    };
    return (
      <Button as="a" href={href} appearance={appearance} onClick={beiKlick} {...(aktion.icon === undefined ? {} : { icon: aktion.icon })}>
        {aktion.label}
      </Button>
    );
  }
  return (
    <Button appearance={appearance} onClick={aktion.onClick} {...(aktion.icon === undefined ? {} : { icon: aktion.icon })}>
      {aktion.label}
    </Button>
  );
}

/** "Vorgangsnummer: <id>" mit Kopierknopf; scheitert die Zwischenablage, bleibt die Nummer markierbar. */
export function Vorgang({ nummer, eingebettet }: { readonly nummer: string; readonly eingebettet: boolean }): ReactElement {
  const own = useStyles();
  const [stand, setStand] = useState<'bereit' | 'kopiert' | 'gescheitert'>('bereit');
  useEffect(() => setStand('bereit'), [nummer]);
  const kopieren = (): void => {
    const scheitern = (): void => setStand('gescheitert');
    try {
      void (navigator.clipboard?.writeText(nummer) ?? Promise.reject(new Error('keine Zwischenablage'))).then(() => setStand('kopiert'), scheitern);
    } catch {
      scheitern();
    }
  };
  return (
    <div className={mergeClasses(own.vorgang, eingebettet && own.eingebettetVorgang)} data-vorgangsnummer={nummer}>
      <Caption1>
        Vorgangsnummer: <span className={own.nummer}>{nummer}</span>
      </Caption1>
      <Button type="button" size="small" appearance="transparent" aria-label={stand === 'kopiert' ? 'Vorgangsnummer kopiert' : 'Vorgangsnummer kopieren'} onClick={kopieren}>
        {stand === 'kopiert' ? 'Kopiert' : 'Kopieren'}
      </Button>
      <span aria-live="polite">
        {stand === 'kopiert' ? <span className={own.verborgen}>Vorgangsnummer kopiert.</span> : undefined}
        {stand === 'gescheitert' ? <Caption1>Kopieren ging nicht. Markiere die Nummer und kopiere sie selbst.</Caption1> : undefined}
      </span>
    </div>
  );
}

/**
 * Die Vorgangszeilen zu Meldungen, die in einer Statusregion stehen. Sie gehoeren HINTER die
 * Region, nicht in sie: die Zeile bringt ihre eigene Ansage ("Vorgangsnummer kopiert.") mit, und
 * eine Live-Region in einer Live-Region liest jeder Screenreader anders (wie KONTAKT in #540).
 */
export function VorgangNach({ meldungen }: { readonly meldungen: readonly (PlainMessage | undefined)[] }): ReactElement | null {
  const nummern = [...new Set(meldungen.flatMap((meldung) => (meldung?.correlationId === undefined ? [] : [meldung.correlationId])))];
  if (nummern.length === 0) return null;
  return (
    <>
      {nummern.map((nummer) => (
        <Vorgang key={nummer} nummer={nummer} eingebettet />
      ))}
    </>
  );
}

function Titel({ ebene, className, titelRef, children }: { readonly ebene: 2 | 3 | 4; readonly className: string; readonly titelRef: RefObject<HTMLHeadingElement | null>; readonly children: string }): ReactElement {
  const Ueberschrift = `h${ebene}` as const;
  return (
    <Ueberschrift className={className} ref={titelRef} tabIndex={-1}>
      {children}
    </Ueberschrift>
  );
}

/**
 * Meldet den Titel als Seitenueberschrift, mit Vorrang vor der des Bildschirms
 * (siehe useSeitenueberschriftVorrang). Eine eigene Komponente, damit nur
 * "nicht da" meldet: ein Leer- oder Fehlerzustand verdraengte sonst die h1,
 * die sein Bildschirm gemeldet hat.
 */
function Seitenueberschrift({ titel }: { readonly titel: string }): null {
  useSeitenueberschriftVorrang(titel);
  return null;
}

/**
 * Wie oben, aber mit Ruecknahme beim Aushaengen: eine Fehlerseite verschwindet
 * mit "Erneut versuchen" auf demselben Pfad, und das Skelett braucht wieder die
 * h1 des Rahmens und dessen Fenstertitel (Codex-Cloud-P2 #583). Der Bildschirm
 * meldet waehrend Laden und Fehler keinen Namen, darum loescht er diese Meldung
 * nicht im selben Durchlauf.
 */
function SeitenueberschriftAufZeit({ titel }: { readonly titel: string }): null {
  useSeitenueberschrift(titel);
  // Mit der Seite haengt auch "Erneut versuchen" aus; der Fokus fiele auf BODY
  // (WCAG 2.4.3). Dann geht er auf den Hauptbereich wie beim Seitenwechsel --
  // dort steht die h1 des Rahmens zuerst. Erst im naechsten Bild: beim
  // Aushaengen steht der Knopf noch im Dokument.
  useEffect(
    () => () => {
      requestAnimationFrame(() => {
        const aktiv = document.activeElement;
        if (aktiv === null || aktiv === document.body) document.getElementById('hauptbereich')?.focus();
      });
    },
    [],
  );
  return null;
}

/**
 * Erscheint "nicht da" nach einer Handlung (etwa Umbenennen, und die Sammlung
 * ist inzwischen beendet), ist der Ausloeser der Handlung mit der Seite
 * verschwunden, und der Fokus faellt auf BODY (WCAG 2.4.3). Dann geht er auf
 * die h1. Geprueft nach dem Einhaengen und im naechsten Bild: ein Dialog gibt
 * den Fokus beim Aushaengen zurueck -- ins Leere. Steht er woanders (nach einer
 * Navigation auf main), bleibt er dort.
 */
function FokusAuffangen({ ziel }: { readonly ziel: RefObject<HTMLHeadingElement | null> }): null {
  useEffect(() => {
    const auffangen = (): void => {
      const aktiv = document.activeElement;
      if (aktiv === null || aktiv === document.body) ziel.current?.focus();
    };
    auffangen();
    const bild = requestAnimationFrame(auffangen);
    return () => cancelAnimationFrame(bild);
  }, [ziel]);
  return null;
}

export function Zustand({ art, titel, saetze, haupt, neben, ebene, status = false, eingebettet = false, fokus = false, vorgangsnummer, seite = false }: ZustandProps): ReactElement {
  const own = useStyles();
  const titelRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    if (fokus) {
      titelRef.current?.focus();
    }
  }, [fokus]);

  const inhalt = (
    <>
      {saetze.length === 0 ? undefined : (
        <div className={mergeClasses(own.saetze, eingebettet && own.eingebetteteSaetze)}>
          {saetze.map((satz) => (
            <Body1 key={satz}>{satz}</Body1>
          ))}
        </div>
      )}
      {haupt === undefined ? undefined : (
        <div className={mergeClasses(own.aktionen, eingebettet && own.eingebettetAktionen)}>
          <Aktion aktion={haupt} haupt={!eingebettet} />
          {neben === undefined ? undefined : <Aktion aktion={neben} haupt={false} />}
        </div>
      )}
    </>
  );
  const vorgang = vorgangsnummer === undefined ? undefined : <Vorgang nummer={vorgangsnummer} eingebettet={eingebettet} />;

  // "Nicht da" ist die ganze Seite (v4 v13): der Titel wird die h1 (der Rahmen
  // nimmt seine zurueck, der Bildschirm traegt seine h1 ohnehin im Inhalt),
  // darunter die Karte. Die Aufrufer bleiben unveraendert. Wer eine Ebene
  // ausdruecklich nennt, steht IN einer Seite und behaelt den Abschnittstitel.
  // Keine Live-Region um eine ganze Seite: sie traegt der Bildschirm, wo noetig;
  // die Vorgangszeile steht darum mit in der Karte.
  if ((art === 'nicht-da' || seite) && !eingebettet && ebene === undefined) {
    return (
      <div data-zustand={art}>
        {art === 'nicht-da' ? <Seitenueberschrift titel={titel} /> : <SeitenueberschriftAufZeit titel={titel} />}
        <FokusAuffangen ziel={titelRef} />
        <h1 className={own.seitentitel} ref={titelRef} tabIndex={-1}>
          {titel}
        </h1>
        <div className={own.karte}>
          <div className={own.block}>
            <Symbol art={art} />
            {inhalt}
            {vorgang}
          </div>
        </div>
      </div>
    );
  }

  // Die Vorgangszeile steht hinter dem Block: traegt er eine Statusregion, laege sie sonst darin.
  return (
    <>
      <div
        className={mergeClasses(own.block, eingebettet && own.eingebettet)}
        data-zustand={art}
        {...(status ? { role: 'status', 'aria-live': 'polite' as const } : {})}
      >
        {eingebettet ? undefined : <Symbol art={art} />}
        {eingebettet && ebene === undefined ? (
          <Body1Strong>{titel}</Body1Strong>
        ) : eingebettet ? (
          <Titel ebene={ebene!} className={own.eingebetteterTitel} titelRef={titelRef}>
            {titel}
          </Titel>
        ) : (
          <Titel ebene={ebene ?? 2} className={mergeClasses(own.titel, art === 'fehler' && own.titelFehler)} titelRef={titelRef}>
            {titel}
          </Titel>
        )}
        {inhalt}
      </div>
      {vorgang}
    </>
  );
}
